import { normalizeName } from '@obraplan/core/domain/shared/naming';
import type { Slab, SlabKind, SlabRegion } from '@obraplan/core/domain/takeoff/slab';
import { paraTexto } from './formato';
import { proximoNomeDaPeca } from './fundacao-da-prancha';

/**
 * As contas do radier e da laje na prancha, sem tela (Fase 27, etapa 3 — D7, RN-369).
 *
 * Moram à parte do desenho para que o que a prancha diz — o próximo nome, a espessura no
 * rótulo — seja texto que se testa sem montar a prancha.
 */

/**
 * As cores do radier e da laje, na prancha e na legenda. Não seguem o tema, pelo mesmo motivo
 * das do resto do traçado: a prancha é papel branco. Índigo para o radier, que é fundação e
 * fica ao lado do bloco e do baldrame; azul-céu para a laje, que é do andar.
 */
export const COR_DO_RADIER = '#4f46e5';
export const COR_DA_LAJE = '#0369a1';

export const corDaLaje = (kind: SlabKind): string =>
  kind === 'RADIER' ? COR_DO_RADIER : COR_DA_LAJE;

/** A espessura de cada peça quando nasce, em centímetros: 25 de radier, 10 de laje (D7). */
export const ESPESSURA_PADRAO_EM_CM: Readonly<Record<SlabKind, number>> = {
  RADIER: 25,
  LAJE: 10,
};

/** Metros em centímetros, com uma casa no máximo — "0,25" vira 25, e não 25,000000000000004. */
export const emCentimetros = (metros: number): number => Math.round(metros * 1000) / 10;

/** "25 cm", "12,5 cm" — a espessura como a obra fala. */
export const espessuraEmTexto = (metros: number): string =>
  `${paraTexto(emCentimetros(metros))} cm`;

/** O rótulo da peça na prancha: o nome e a espessura dela — "L1 · 10 cm". */
export function rotuloDaLaje(laje: Pick<Slab, 'name' | 'thickness'>): string {
  return `${laje.name} · ${espessuraEmTexto(laje.thickness)}`;
}

/** O rótulo da região: só a espessura, que é o que ela diz — "25 cm". */
export const rotuloDaRegiao = (regiao: Pick<SlabRegion, 'thickness'>): string =>
  espessuraEmTexto(regiao.thickness);

/** "L1", "L2"… — o próximo nome livre, na sequência da última laje desenhada. */
export const proximoNomeDeLaje = (lajes: readonly { readonly name: string }[]): string =>
  proximoNomeDaPeca(lajes, 'L');

/**
 * "Radier", e depois "Radier 2", "Radier 3": a fundação costuma ter um radier só, e o nome
 * dele é o da peça. Pula o que já existe, porque a sugestão recusada seria pior que nenhuma.
 */
export function proximoNomeDeRadier(radiers: readonly { readonly name: string }[]): string {
  const usados = new Set(radiers.map((radier) => normalizeName(radier.name)));
  if (!usados.has(normalizeName('Radier'))) return 'Radier';
  for (let numero = 2; numero < 10_000; numero += 1) {
    const candidato = `Radier ${String(numero)}`;
    if (!usados.has(normalizeName(candidato))) return candidato;
  }
  return `Radier ${String(radiers.length + 1)}`;
}
